import { useEffect, useMemo, useState } from "react";
import { NavLink, useLocation } from "react-router-dom";
import { ChevronDown, ChevronRight, Search, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { visibleGroups, groupIdForPath, parentPathForPath, navTestId } from "@/crm/navigation";

// Groupes REPLIÉS (défaut : tout déplié) et sous-menus DÉPLIÉS (défaut : tout
// replié). Les deux ensembles retiennent l'exception, jamais la règle : une
// clé absente vaut donc l'état par défaut de son niveau.
const COLLAPSED_KEY = "prospee_nav_collapsed";
const SUBOPEN_KEY = "prospee_nav_subopen";

// localStorage peut lever (navigation privée, cookies bloqués) : la nav doit
// rester utilisable, on retombe simplement sur les états par défaut.
const readSet = (key) => {
  try {
    const raw = localStorage.getItem(key);
    return raw ? new Set(JSON.parse(raw)) : new Set();
  } catch { return new Set(); }
};

const persistSet = (key, set) => {
  try { localStorage.setItem(key, JSON.stringify([...set])); } catch { /* non bloquant */ }
};

const norm = (s) => s.normalize("NFD").replace(/[\u0300-\u036f]/g, "").toLowerCase();

const FOCUS = "focus:outline-none focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-[#4f9dff]";

/**
 * Navigation de la barre latérale.
 *
 * Trois présentations, choisies par la largeur du shell en CSS (paliers
 * `shell-*` de tailwind.config.js) et non par une mesure JS — le premier
 * rendu est déjà juste :
 *
 *   < 760 px   tiroir mobile : navigation complète (filtre, groupes, libellés)
 *   760–1079   rail d'icônes : libellés, filtre et en-têtes de groupe masqués,
 *              icônes centrées, groupes séparés par un filet, `title` en infobulle
 *   ≥ 1080     menu étendu — sauf si l'utilisateur l'a réduit (`railPref`,
 *              bouton « Réduire le menu » du pied de la barre), auquel cas le
 *              rail reste.
 *
 * Un seul arbre DOM pour les trois : ce sont les classes qui masquent. Les
 * chaînes de classes sont écrites en entier (pas de `shell-lg:${x}`) pour que
 * le scanner Tailwind les trouve.
 */
export default function SidebarNav({ role, onNavigate, railPref = false }) {
  const { pathname } = useLocation();
  const [collapsed, setCollapsed] = useState(() => readSet(COLLAPSED_KEY));
  const [subOpen, setSubOpen] = useState(() => readSet(SUBOPEN_KEY));
  const [query, setQuery] = useState("");

  const groups = visibleGroups(role);
  const activeGroupId = groupIdForPath(pathname, role);
  const activeParent = parentPathForPath(pathname, role);

  // Le groupe de la page courante ne reste jamais replié : sinon l'utilisateur
  // ne voit plus où il se trouve après un rechargement.
  useEffect(() => {
    if (!activeGroupId) return;
    setCollapsed((prev) => {
      if (!prev.has(activeGroupId)) return prev;
      const next = new Set(prev);
      next.delete(activeGroupId);
      persistSet(COLLAPSED_KEY, next);
      return next;
    });
  }, [activeGroupId]);

  // Même raison pour le sous-menu de la page courante : on arrive sur
  // /parametres/debug avec l'entrée dépliée, pas cachée sous son parent.
  useEffect(() => {
    if (!activeParent) return;
    setSubOpen((prev) => {
      if (prev.has(activeParent)) return prev;
      const next = new Set(prev);
      next.add(activeParent);
      persistSet(SUBOPEN_KEY, next);
      return next;
    });
  }, [activeParent]);

  // En rail le filtre n'a pas de place pour s'afficher : on le vide pour ne
  // pas masquer des entrées de façon invisible.
  useEffect(() => { if (railPref) setQuery(""); }, [railPref]);

  const toggle = (id) => setCollapsed((prev) => {
    const next = new Set(prev);
    next.has(id) ? next.delete(id) : next.add(id);
    persistSet(COLLAPSED_KEY, next);
    return next;
  });

  const toggleSub = (to) => setSubOpen((prev) => {
    const next = new Set(prev);
    next.has(to) ? next.delete(to) : next.add(to);
    persistSet(SUBOPEN_KEY, next);
    return next;
  });

  const q = norm(query.trim());
  const filtered = useMemo(() => {
    if (!q) return groups;
    return groups
      .map((g) => ({
        ...g,
        // Une sous-entrée trouvée s'affiche sous son parent (gardé comme
        // contexte) ; un parent trouvé garde toutes ses sous-entrées.
        items: g.items
          .map((i) => {
            if (norm(i.label).includes(q)) return i;
            const children = (i.children || []).filter((c) => norm(c.label).includes(q));
            return children.length ? { ...i, children } : null;
          })
          .filter(Boolean),
      }))
      .filter((g) => g.items.length > 0);
  }, [groups, q]);

  // Éléments du mode « étendu » (masqués en rail) et du mode « rail » seul.
  const FULL_BLOCK = railPref ? "shell-sm:hidden" : "shell-sm:hidden shell-lg:block";
  const FULL_FLEX = railPref ? "shell-sm:hidden" : "shell-sm:hidden shell-lg:flex";
  const FULL_INLINE_FLEX = railPref ? "shell-sm:hidden" : "shell-sm:hidden shell-lg:inline-flex";
  const RAIL_CENTER = railPref
    ? "shell-sm:justify-center shell-sm:px-0"
    : "shell-sm:justify-center shell-sm:px-0 shell-lg:justify-start shell-lg:px-3";
  const RAIL_RULE = railPref
    ? "shell-sm:mt-1 shell-sm:border-t shell-sm:border-[#1e293b] shell-sm:pt-2.5"
    : "shell-sm:mt-1 shell-sm:border-t shell-sm:border-[#1e293b] shell-sm:pt-2.5 shell-lg:mt-0 shell-lg:border-t-0 shell-lg:pt-0";
  // Liste repliée : masquée en étendu, mais toujours visible en rail (son
  // en-tête, seul moyen de la déplier, n'y est pas affiché).
  const COLLAPSED_LIST = railPref ? "hidden shell-sm:flex" : "hidden shell-sm:flex shell-lg:hidden";
  // Sous-entrées : en retrait quand les libellés sont visibles (tiroir mobile,
  // menu étendu), au ras des autres icônes en rail.
  const CHILD_INDENT = railPref ? "pl-[26px] shell-sm:pl-0" : "pl-[26px] shell-sm:pl-0 shell-lg:pl-[26px]";
  // Place du chevron de dépliage sur l'entrée parente : inutile en rail, où le
  // bouton n'est pas affiché (l'icône y est centrée dans toute la largeur).
  const PARENT_PAD = railPref ? "pr-9 shell-sm:pr-0" : "pr-9 shell-sm:pr-0 shell-lg:pr-9";

  const linkClasses = ({ isActive }) => cn(
    "relative flex min-h-[44px] w-full items-center gap-[11px] rounded-[10px] px-3 text-left text-sm transition-colors duration-150",
    RAIL_CENTER, FOCUS,
    isActive ? "bg-[#005bd3] font-semibold text-white" : "font-medium text-[#94a3b8] hover:bg-white/[.08] hover:text-white"
  );

  // Une entrée (écran ou sous-écran) : même rendu aux deux niveaux, seule
  // l'indentation du conteneur les distingue.
  const renderLink = (item, extraClass) => (
    <NavLink
      key={item.to}
      to={item.to}
      end={item.end}
      onClick={onNavigate}
      title={item.label}
      aria-label={item.label}
      data-testid={navTestId(item.label)}
      className={(state) => cn(linkClasses(state), extraClass)}
    >
      {({ isActive }) => (
        <>
          {/* L'état actif n'est jamais porté par la couleur seule :
              liseré blanc à gauche + aria-current posé par NavLink. */}
          {isActive && (
            <span className={cn("absolute left-0 top-1/2 h-5 w-[3px] -translate-y-1/2 rounded-r-[3px] bg-white", FULL_BLOCK)} />
          )}
          <item.icon size={17} className="shrink-0" />
          <span className={cn("min-w-0 flex-1 truncate", FULL_BLOCK)}>{item.label}</span>
          {item.badge != null && (
            <span className={cn("rounded-full bg-white/[.14] px-1.5 py-px font-mono text-[11px] text-[#e2e8f0]", FULL_INLINE_FLEX)}>
              {item.badge}
            </span>
          )}
        </>
      )}
    </NavLink>
  );

  return (
    <>
      <div className={cn("shrink-0 px-3 pb-1 pt-3", FULL_BLOCK)}>
        {/* Ce champ vit dans SON PROPRE <form>, et c'est la pièce maîtresse du
            correctif. Chrome regroupe tous les champs SANS formulaire de la page
            dans un formulaire fictif unique : dès qu'un écran affichait un champ
            mot de passe hors <form> (Mode Debug, clés API, purge…), le
            gestionnaire de mots de passe voyait « identifiant + mot de passe » et
            injectait l'email de connexion ICI — le menu s'affichait alors vide
            (« Aucun écran ne correspond »). Un <form> sans champ mot de passe
            n'est pas un formulaire de connexion : le champ sort du lot commun et
            n'est plus jamais rempli, quel que soit l'écran affiché à droite.
            autoComplete=off et les data-* restent utiles pour les suggestions
            simples et pour 1Password / LastPass / Dashlane, qui ignorent l'un
            comme l'autre. */}
        <form onSubmit={(e) => e.preventDefault()} role="search" autoComplete="off">
          <label className={cn("flex h-10 items-center gap-2 rounded-[10px] border border-[#22304a] bg-white/[.06] px-2.5 transition-colors",
            "focus-within:border-[#4f9dff]")}>
            <Search size={15} className="shrink-0 text-[#64748b]" />
            <input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Aller à…"
              data-testid="nav-filter"
              aria-label="Filtrer la navigation"
              type="search"
              name="prospee-nav-filter"
              autoComplete="off"
              autoCorrect="off"
              autoCapitalize="off"
              spellCheck="false"
              data-lpignore="true"
              data-1p-ignore=""
              data-form-type="other"
              className="min-w-0 flex-1 bg-transparent text-sm text-[#e2e8f0] outline-none placeholder:text-[#64748b] [&::-webkit-search-cancel-button]:appearance-none"
            />
            {query && (
              <button type="button" onClick={() => setQuery("")} aria-label="Effacer le filtre"
                className="shrink-0 text-[#64748b] hover:text-[#cbd5e1]">
                <X size={14} />
              </button>
            )}
          </label>
        </form>
      </div>

      <nav className="no-scrollbar flex min-h-0 flex-1 flex-col gap-1.5 overflow-y-auto px-3 pb-3.5 pt-2.5" aria-label="Navigation principale">
        {/* Pendant une recherche les groupes restent ouverts : l'utilisateur
            cherche une entrée, pas un groupe. */}
        {filtered.map((group, gi) => {
          const isCollapsed = !q && collapsed.has(group.id);
          const hasActive = group.id === activeGroupId;
          return (
            <div key={group.id} className={cn("flex flex-col gap-0.5", gi > 0 && RAIL_RULE)}>
              <button
                type="button"
                onClick={() => toggle(group.id)}
                aria-expanded={!isCollapsed}
                aria-controls={`nav-group-${group.id}`}
                data-testid={`nav-group-${group.id}`}
                className={cn(
                  "flex h-[34px] w-full items-center gap-2 rounded-[9px] px-2.5",
                  "text-[11px] font-bold uppercase tracking-[.08em] transition-colors hover:bg-white/[.05]",
                  FULL_FLEX, FOCUS,
                  hasActive ? "text-[#7fb4ff]" : "text-[#64748b]"
                )}
              >
                <group.icon size={14} className="shrink-0" />
                <span className="flex-1 truncate text-left">{group.label}</span>
                {isCollapsed ? <ChevronRight size={14} className="shrink-0" /> : <ChevronDown size={14} className="shrink-0" />}
              </button>

              <div id={`nav-group-${group.id}`} className={cn("flex-col gap-0.5", isCollapsed ? COLLAPSED_LIST : "flex")}>
                {group.items.map((item) => {
                  if (!item.children?.length) return renderLink(item);
                  // Sous-menu replié tant qu'on ne l'ouvre pas — sauf pendant
                  // une recherche, où c'est l'entrée cherchée qu'on veut voir.
                  const subId = `nav-sub-${navTestId(item.label)}`;
                  const open = !!q || subOpen.has(item.to);
                  return (
                    <div key={item.to} className="flex flex-col gap-0.5">
                      <div className="relative flex items-center">
                        {renderLink(item, PARENT_PAD)}
                        {/* Bouton distinct du lien : ouvrir le sous-menu ne doit
                            pas quitter la page où l'on est. */}
                        <button
                          type="button"
                          onClick={() => toggleSub(item.to)}
                          aria-expanded={open}
                          aria-controls={subId}
                          aria-label={`${open ? "Masquer" : "Afficher"} les sous-entrées de ${item.label}`}
                          data-testid={`${navTestId(item.label)}-toggle`}
                          className={cn(
                            "absolute right-1.5 h-7 w-7 items-center justify-center rounded-md",
                            "text-[#94a3b8] transition-colors hover:bg-white/[.12] hover:text-white",
                            FULL_INLINE_FLEX, FOCUS
                          )}
                        >
                          {open ? <ChevronDown size={14} /> : <ChevronRight size={14} />}
                        </button>
                      </div>
                      <div id={subId} className={cn("flex-col gap-0.5", CHILD_INDENT, open ? "flex" : COLLAPSED_LIST)}>
                        {item.children.map((child) => renderLink(child))}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          );
        })}

        {/* Sortie de secours : un menu vide est un cul-de-sac, on offre toujours
            le moyen de revenir à la navigation complète en un clic. */}
        {filtered.length === 0 && (
          <div className={cn("px-3 py-2.5", FULL_BLOCK)} data-testid="nav-filter-empty">
            <p className="m-0 text-[13px] text-[#64748b]">Aucun écran ne correspond.</p>
            <button
              type="button"
              onClick={() => setQuery("")}
              data-testid="nav-filter-reset"
              className="mt-1.5 text-xs text-[#4f9dff] underline underline-offset-2 hover:text-[#7fb4ff]"
            >
              Afficher tout le menu
            </button>
          </div>
        )}
      </nav>
    </>
  );
}
